import { useMemo, useEffect } from 'react';
import { Navigate } from 'react-router-dom';

import { Loader } from '@hivebrite/design-system/Loader';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { type Event } from 'hb-react/shared/apps/Events';
import { EventZoomMeeting } from 'hb-react/shared/apps/Events/components/EventZoomMeeting';
import { useEvent } from 'hb-react/shared/apps/Events/hooks/useEvent';
import { useZoomPageErrorHandling } from 'hb-react/shared/apps/Events/pages/Zoom/hooks/useZoomPageErrorHandling';
import { userAllowedToJoin } from 'hb-react/shared/apps/Events/utils/userAllowedToJoin';
import { ZoomMeetingGlobalStyle } from 'hb-react/shared/apps/Zoom/components/ZoomMeetingGlobalStyle';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';

export const ZoomPage = () => {
  const { currentUser } = useDefaultAppContexts();
  const { event, isLoading: isLoadingEvent } = useEvent();
  const { flagEvStandaloneZoom } = useFeatureFlag();
  const { topicId, whitelabelURL } = useTopicContext();

  const isSharedArrayBufferAvailable = typeof SharedArrayBuffer === 'function';

  // Check for SharedArrayBuffer availability and reload if not available
  useEffect(() => {
    if (!event) {
      return;
    }

    const sessionStorageKey = `zoom_reload_attempted_${event.id}`;
    const hasAttemptedReload = sessionStorage.getItem(sessionStorageKey);

    // Our server will only add the required headers for SharedArrayBuffer when
    // loading the event details or event online meeting page directly. If you
    // landed in any other of the SPA page and got to this page through frontend
    // navigation the SharedArrayBuffer will not be available. Forcing the page to
    // reload will force the server to add the required headers.
    // See `app/controllers/concerns/response_headers_concern.rb` for more details
    if (!isSharedArrayBufferAvailable) {
      if (hasAttemptedReload) {
        // If we've already attempted a reload, don't reload again
        // The browser simply doesn't support SharedArrayBuffers even with proper headers
        sessionStorage.removeItem(sessionStorageKey);
      } else {
        // Set flag in session storage to prevent multiple reloads
        sessionStorage.setItem(sessionStorageKey, 'true');
        // Reload the page to force the server to enable correct headers
        window.location.reload();
      }
    }
  }, [isSharedArrayBufferAvailable, event]);

  const isUserAllowedToJoin = useMemo(
    () =>
      !!event && userAllowedToJoin(event, currentUser, flagEvStandaloneZoom),
    [event, currentUser, flagEvStandaloneZoom],
  );

  const leaveUrl = useMemo(() => {
    if (!event) {
      return '';
    }

    return topicId && !whitelabelURL
      ? Routes.topic_event_online_completed_path(topicId, event.id)
      : Routes.network_events_event_online_completed_path(event.id);
  }, [event, topicId, whitelabelURL]);

  // Handle error messages
  useZoomPageErrorHandling({
    event,
    isUserAllowedToJoin,
    isLoadingEvent,
  });

  const shouldRedirect = !event || !isUserAllowedToJoin;

  if (isLoadingEvent) {
    return <Loader />;
  }

  if (shouldRedirect) {
    return <Navigate to=".." />;
  }

  return (
    <>
      <ZoomMeetingGlobalStyle />
      <EventZoomMeeting event={event as Event} leaveUrl={leaveUrl} />
    </>
  );
};
